import {
    Button,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow
} from "@mui/material";
import PropTypes from "prop-types";
import {API_URL, COLLECTION_ROLE} from "../../constants";
import {makeRequest} from "../../services/requestService";
import React from "react";
import {useTranslation} from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import SearchUser from "../Search/SearchUser";

const ROWS_PER_PAGE = 5;

async function loadData(collection, page = 0) {
    const url = `${API_URL}/collection/${collection}/users?page=${page}&size=${ROWS_PER_PAGE}`;
    const response = await makeRequest(url);

    if (response.status !== 200) return [];

    return response.data;
}

async function postMember(collectionId, userId, role) {
    const data = {role, userId};
    const url = `${API_URL}/collection/${collectionId}/user`;
    const response = await makeRequest(url, 'POST', data, {
        'Content-Type': 'application/json',
    });

    return response.data;
}

async function putMember(collectionId, userId, role) {
    const data = {role};
    const url = `${API_URL}/collection/${collectionId}/user/${userId}`;
    const response = await makeRequest(url, 'PUT', data, {
        'Content-Type': 'application/json',
    });

    return response.data;
}

async function deleteMember(collectionId, userId) {
    const url = `${API_URL}/collection/${collectionId}/user/${userId}`;
    const response = await makeRequest(url, 'DELETE');
    return response.data;
}

export default function MemberList(props) {
    const {t} = useTranslation('common');
    const [users, setUsers] = React.useState(new Map());
    const [page, setPage] = React.useState(0);
    const [totalItems, setTotalItems] = React.useState(0);
    const [newUserId, setNewUserId] = React.useState(-1);
    const [newUserRole, setNewUserRole] = React.useState(COLLECTION_ROLE.READER);
    const [updateUsers, setUpdateUsers] = React.useState(0);

    React.useEffect(async () => {
        const {data, totalItems} = await loadData(props.collection, page);
        data.forEach((item) => {
            item.original = {...item}
            if (users && users.has(item.id)) {
                item.role = users.get(item.id).role;
            }
        });
        setUsers(data.reduce((acc, cur) => acc.set(cur.id, cur), new Map()));
        setTotalItems(totalItems);
    }, [props.collection, page, updateUsers]);

    async function handleAddMember() {
        await postMember(props.collection, newUserId, newUserRole);
        setUpdateUsers(updateUsers + 1);
    }

    async function handleUpdateMember(userId, role) {
        await putMember(props.collection, userId, role);
        setUpdateUsers(updateUsers + 1);
    }

    async function handleDeleteMember(userId) {
        await deleteMember(props.collection, userId);
        setUpdateUsers(updateUsers + 1);
    }

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <SearchUser size="small" onChange={(value) => setNewUserId(value?.id ?? -1)}/>
                <Select
                    size="small"
                    sx={{width: 200}}
                    fullWidth
                    onChange={(event) => setNewUserRole(event.target.value)}
                    value={newUserRole}
                >
                    {Object.values(COLLECTION_ROLE).filter(val => val !== COLLECTION_ROLE.OWNER).map((val) => (
                        <MenuItem key={val}
                                  value={val}>{t(`main.collections.role.${val.toLowerCase()}`)}</MenuItem>
                    ))}
                </Select>
                <Button size="small" disabled={newUserId < 0} variant="contained" onClick={() => handleAddMember()}>
                    Ajouter
                </Button>
            </Stack>
            <Paper>
                <TableContainer sx={{maxHeight: 440}}>
                    <Table stickyHeader size="small" aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell key="username">
                                    Username
                                </TableCell>
                                <TableCell key="role">
                                    Role
                                </TableCell>
                                <TableCell key="actions" align="right">
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...users.entries()].map(([id, row]) => {
                                return (
                                    <TableRow key={id} hover role="checkbox" tabIndex={-1}
                                              sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                    >
                                        <TableCell key="username">
                                            {row.username}
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                key="role"
                                                size="small"
                                                sx={{width: 200}}
                                                disabled={row.role === COLLECTION_ROLE.OWNER}
                                                value={row.role}
                                                onChange={(event) => {
                                                    users.get(id).role = event.target.value;
                                                    setUsers(new Map(users));
                                                }}
                                            >
                                                {Object.values(COLLECTION_ROLE).map((val) => (
                                                    <MenuItem key={val} value={val}
                                                              disabled={val === COLLECTION_ROLE.OWNER}>
                                                        {t(`main.collections.role.${val.toLowerCase()}`)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>
                                        <TableCell key="actions" align="right">
                                            {row.role !== COLLECTION_ROLE.OWNER &&
                                                <IconButton onClick={() => handleDeleteMember(row.id)}>
                                                    <DeleteIcon color="error"/>
                                                </IconButton>
                                            }
                                            <IconButton
                                                disabled={row.role === row.original.role}
                                                onClick={() => handleUpdateMember(row.id, row.role)}
                                            >
                                                <DoneIcon/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    size="small"
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    component="div"
                    count={totalItems}
                    rowsPerPage={ROWS_PER_PAGE}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                />
            </Paper>
        </Stack>
    )
}

MemberList.propTypes = {
    collection: PropTypes.string.isRequired,
    tableProps: PropTypes.shape(TableContainer.propTypes),
}